/**
 * React hook for voice message recording.
 *
 * Composes the three Layer 1 classes (MicrophoneManager, AudioRecorder,
 * WaveformAnalyzer) into a single hook that exposes:
 *   - Recording lifecycle methods: `requestMic`, `record`, `stop`, `cancel`
 *   - Real-time state: `duration`, `liveAmplitude`, `waveformBars`
 *   - Status flags: `voiceRecordingStatus`, `isAtMaxDuration`, `isNearMaxDuration`
 *
 * Integrates with the Zustand `voiceStore` for global coordination so that
 * only one recording or playback can be active at a time.
 *
 * **Lifecycle**:
 * 1. `requestMic()` — called on touch-start to acquire the mic early.
 * 2. `record()` — called on long-press to start MediaRecorder + waveform sampling.
 * 3. `stop()` — called on release to finalize the File and release hardware.
 * 4. `cancel()` — called on swipe-away to discard data and release hardware.
 */

import { useState, useRef, useCallback, useEffect } from "react";

import { useNavigationState } from "@/zustand/NavigationState";

import { MicrophoneManager } from "./MicrophoneManager";
import { AudioRecorder } from "./AudioRecorder";
import { WaveformAnalyzer } from "./WaveformAnalyzer";
import { useVoiceStore } from "./voiceStore";
import {
  MAX_VOICE_RECORDING_DURATION,
  MAX_WAVEFORM_BARS,
  DEFAULT_BAR_HEIGHT,
  WAVEFORM_THROTTLE_MS,
} from "./constants";

import type { RecordingStatus } from "./types";

// ── Helpers ──

/**
 * Downsamples a raw peaks array into a fixed number of waveform bars.
 *
 * - If fewer peaks than target: pads with `defaultValue`.
 * - If more peaks than target: averages each bucket.
 * - If empty: returns an array filled with `defaultValue`.
 *
 * @param peaks - Raw peak values (0–1) collected during recording.
 * @param targetBars - The desired number of output bars.
 * @param defaultValue - The fill value for empty/padding bars.
 * @returns An array of exactly `targetBars` normalized values.
 */
function downsample(
  peaks: number[],
  targetBars: number = MAX_WAVEFORM_BARS,
  defaultValue: number = DEFAULT_BAR_HEIGHT,
): number[] {
  if (!peaks.length) {
    return new Array(targetBars).fill(defaultValue);
  }

  if (peaks.length <= targetBars) {
    const padded = [...peaks];
    while (padded.length < targetBars) {
      padded.push(defaultValue);
    }
    return padded;
  }

  const downsampled = new Array(targetBars);
  const bucketSize = peaks.length / targetBars;

  for (let i = 0; i < targetBars; i++) {
    const start = Math.floor(i * bucketSize);
    const end = Math.floor((i + 1) * bucketSize);

    let sum = 0;
    let count = 0;
    for (let j = start; j < end; j++) {
      sum += peaks[j];
      count++;
    }

    downsampled[i] = count > 0 ? sum / count : defaultValue;
  }

  return downsampled;
}

// ── Hook ──

/** Return type of the useVoiceRecorder hook */
export interface UseVoiceRecorderReturn {
  /** Current recording lifecycle status */
  voiceRecordingStatus: RecordingStatus;
  /** Elapsed recording time in milliseconds */
  duration: number;
  /** Real-time amplitude value (0–50) for driving waveform animation */
  liveAmplitude: number;
  /** Downsampled waveform bars (array of MAX_WAVEFORM_BARS normalized 0–1 values) */
  waveformBars: number[];
  /** True when recording has reached the maximum allowed duration */
  isAtMaxDuration: boolean;
  /** True when recording has reached 90% of the maximum allowed duration */
  isNearMaxDuration: boolean;
  /** True when the recording was stopped automatically by hitting the duration limit */
  stoppedAtLimit: boolean;
  /** Acquire the mic early (call on touch-start, before long-press fires) */
  requestMic: () => Promise<void>;
  /** Start recording. Acquires mic if not already acquired (deduplicates) */
  record: () => Promise<void>;
  /** Stop recording and return the audio File with final duration and waveform */
  stop: () => Promise<{ file: File; duration: number; waveform: number[] }>;
  /** Cancel recording and discard all data */
  cancel: () => void;
}

export function useVoiceRecorder(
  maxDuration: number = MAX_VOICE_RECORDING_DURATION,
): UseVoiceRecorderReturn {
  const warningThreshold = maxDuration * 0.9;
  const micManager = MicrophoneManager.getInstance();

  const recorderRef = useRef(new AudioRecorder());
  const analyzerRef = useRef(new WaveformAnalyzer());

  // Zustand
  const {
    recordingChatId,
    recordingStatus,
    startRecordingSession,
    setRecordingStatus,
    endRecordingSession,
  } = useVoiceStore();

  const activeChatId = useNavigationState((s) => s.activeChatId);
  const isOwner = recordingChatId === activeChatId;

  // Local state
  const [duration, setDuration] = useState(0);
  const [waveformBars, setWaveformBars] = useState<number[]>([]);
  const [liveAmplitude, setLiveAmplitude] = useState(0);

  // Refs for animation / interval IDs
  const rafRef = useRef<number | null>(null);
  const waveformIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const startTimeRef = useRef<number | null>(null);
  const peaksRef = useRef<number[]>([]);

  // Holds the file-promise created by AudioRecorder.start().
  // Resolves to a File on stop, or null when the recording is cancelled.
  const filePromiseRef = useRef<Promise<File | null> | null>(null);

  // Set to true when recording stops due to hitting the plan duration limit.
  // Cleared by stop() and cancel(). Used by RtfInput to reliably auto-send.
  const stoppedAtLimitRef = useRef(false);

  // ── Duration timer (rAF loop) ──

  const updateDuration = useCallback(() => {
    if (!startTimeRef.current) {
      return;
    }

    const elapsed = Date.now() - startTimeRef.current;

    if (elapsed >= maxDuration) {
      setDuration(maxDuration);
      // Auto-stop: stop the MediaRecorder, release hardware, and update store.
      // filePromiseRef, startTimeRef, and peaksRef are intentionally kept so
      // the component can still call stop() to retrieve the recorded file
      // with the correct duration and waveform data.
      stoppedAtLimitRef.current = true;
      recorderRef.current.stop();
      micManager.release();
      analyzerRef.current.disconnect();
      endRecordingSession();
      return;
    }

    setDuration(elapsed);
    rafRef.current = requestAnimationFrame(updateDuration);
    // endRecordingSession is a stable Zustand action, micManager is a singleton
  }, [endRecordingSession, maxDuration, micManager]);

  // ── Live amplitude (rAF loop, only while recording) ──

  useEffect(() => {
    if (recordingStatus !== "recording") {
      setLiveAmplitude(0);
      return;
    }

    let frameId: number;
    const tick = () => {
      setLiveAmplitude(analyzerRef.current.sampleAmplitude());
      frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(frameId);
  }, [recordingStatus]);

  // ── Waveform peak sampling (interval, only while recording) ──

  useEffect(() => {
    if (recordingStatus !== "recording") {
      return;
    }

    const id = setInterval(() => {
      const peak = analyzerRef.current.samplePeak();
      peaksRef.current.push(peak);
      setWaveformBars(downsample(peaksRef.current));
    }, WAVEFORM_THROTTLE_MS);

    waveformIntervalRef.current = id;
    return () => {
      clearInterval(id);
      waveformIntervalRef.current = null;
    };
  }, [recordingStatus]);

  // ── Visibility change (mobile resume) ──
  //
  // When the user backgrounds the app and comes back, iOS Safari suspends
  // the AudioContext. We only attempt to resume if a recording is active.
  // The resume() call is wrapped in try-catch because iOS rejects it
  // without a user gesture — in that case we silently ignore the error
  // since the analyzer will reconnect on the next recording.

  useEffect(() => {
    const handler = async () => {
      if (document.visibilityState === "visible") {
        if (recorderRef.current.isRecording) {
          try {
            await analyzerRef.current.resume();
          } catch {
            // iOS may reject AudioContext.resume() without a user gesture
            // after returning from background. Safe to ignore — waveform
            // will just stop updating until next recording.
          }
          rafRef.current = requestAnimationFrame(updateDuration);
        }
      } else if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };

    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [updateDuration]);

  // ── Ownership guard: force-cancel if another chat steals the recording ──

  useEffect(() => {
    // Check hardware state directly — the store's recordingStatus may already
    // be "idle" (reset by startPlayback) while the MediaRecorder is still active.
    if (!isOwner && recorderRef.current.isRecording) {
      forceCleanup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner]);

  // ── Cleanup on unmount ──

  useEffect(() => {
    const recorder = recorderRef.current;
    const analyzer = analyzerRef.current;
    return () => {
      micManager.release();
      recorder.cancel();
      analyzer.destroy();
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      if (waveformIntervalRef.current) {
        clearInterval(waveformIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Internal helpers ──

  /** Resets all local state (timers, peaks, duration) back to initial values */
  const resetLocalState = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    startTimeRef.current = null;
    peaksRef.current = [];
    filePromiseRef.current = null;
    setDuration(0);
    setWaveformBars([]);
  };

  /** Emergency cleanup: cancels recording, releases mic, disconnects analyzer, resets state */
  const forceCleanup = () => {
    // cancel() resolves the file-promise with null (no rejection to swallow)
    stoppedAtLimitRef.current = false;
    recorderRef.current.cancel();
    micManager.release();
    analyzerRef.current.disconnect();
    endRecordingSession();
    resetLocalState();
  };

  // ── Public API ──

  /**
   * Acquire the microphone early (before long-press fires).
   * Updates status to "requesting" while getUserMedia is pending.
   */
  const requestMic = useCallback(async () => {
    if (micManager.getStream()) {
      return;
    }

    setRecordingStatus("requesting");
    try {
      await micManager.acquire();
      // Permission granted — reset to idle. The user must long-press again
      // to actually start recording. record() will set status to "recording".
      setRecordingStatus("idle");
    } catch (err) {
      setRecordingStatus("idle");
      throw err instanceof Error ? err : new Error("Microphone access failed.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Start recording. Acquires mic if not already acquired (deduplicates).
   * The returned promise resolves immediately — call `stop()` to get the File.
   */
  const record = useCallback(async () => {
    // Read fresh from the store — callers like useNewAIChatVoice set activeChatId
    // synchronously before calling record(), but the hook's captured value is one
    // render behind. getState() always reflects the current store value.
    const currentChatId = useNavigationState.getState().activeChatId;
    if (!currentChatId) {
      throw new Error("No active chat");
    }

    // Guard: prevent double-invoke from destroying an in-progress recording
    if (recorderRef.current.isRecording) {
      return;
    }

    // Ensure mic is acquired (returns immediately if already done / deduplicates)
    const stream = await micManager.acquire();

    try {
      // Connect waveform analyzer
      await analyzerRef.current.connect(stream);

      // Start MediaRecorder — returns a Promise<File | null> that resolves with
      // the File on stop, or null if the recording is cancelled.
      filePromiseRef.current = recorderRef.current.start(stream);
    } catch (err) {
      // Clean up already-acquired resources before re-throwing
      micManager.release();
      analyzerRef.current.disconnect();
      throw err;
    }

    // Update global state
    startRecordingSession(currentChatId);

    // Start duration timer
    peaksRef.current = [];
    startTimeRef.current = Date.now();
    rafRef.current = requestAnimationFrame(updateDuration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDuration]);

  /**
   * Stop recording and return the audio File along with the final duration
   * and waveform snapshot.
   *
   * Returns an object so the caller gets the definitive values instead of
   * relying on React state (which may be one render behind).
   */
  const stop = useCallback(async (): Promise<{
    file: File;
    duration: number;
    waveform: number[];
  }> => {
    if (!filePromiseRef.current) {
      throw new Error("No active recording");
    }

    const promise = filePromiseRef.current;

    // Stop recorder — this causes AudioRecorder's onstop to resolve filePromise
    recorderRef.current.stop();

    // Take final waveform snapshot and capture duration before resetting
    const finalBars = downsample(peaksRef.current);
    const finalDuration = startTimeRef.current
      ? Math.min(Date.now() - startTimeRef.current, maxDuration)
      : 0;

    // Wait for the File BEFORE releasing hardware.
    // MediaRecorder.stop() is async — ondataavailable/onstop fire via the event
    // loop. Stopping the MediaStream tracks before those events deliver the final
    // audio data can produce an empty blob (especially on Safari where all data
    // arrives in a single ondataavailable, and under main-thread congestion from
    // incoming-message re-renders).
    //
    // The await resolves to null if the recording was cancelled mid-stop (a
    // cancel() / forceCleanup() racing this stop()). Release hardware and reset
    // state in `finally` so the mic is never leaked even on that path.
    let file: File | null;
    try {
      file = await promise;
    } finally {
      micManager.release();
      analyzerRef.current.disconnect();
      endRecordingSession();
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = null;
      startTimeRef.current = null;
      peaksRef.current = [];
      filePromiseRef.current = null;
      stoppedAtLimitRef.current = false;
      setDuration(0);
      setWaveformBars([]);
    }

    if (!file) {
      // Cancelled mid-stop — no file to send. Surface as a thrown error; every
      // caller awaits stop() inside try/catch and treats this as "no send".
      throw new Error("Recording cancelled");
    }

    return { file, duration: finalDuration, waveform: finalBars };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Cancel recording and discard all data.
   * Releases mic and resets all state.
   */
  const cancel = useCallback(() => {
    // cancel() resolves the file-promise with null — no rejection to swallow.
    stoppedAtLimitRef.current = false;
    recorderRef.current.cancel();
    micManager.release();
    analyzerRef.current.disconnect();
    endRecordingSession();
    resetLocalState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    voiceRecordingStatus: recordingStatus,
    duration,
    liveAmplitude,
    waveformBars,
    isAtMaxDuration: duration >= maxDuration,
    isNearMaxDuration: duration >= warningThreshold,
    stoppedAtLimit: stoppedAtLimitRef.current,
    requestMic,
    record,
    stop,
    cancel,
  };
}
